import React from 'react';
import Form from '@grnhse/seedling/lib/birch/components/forms/form';
import { RadioGroupField } from '@grnhse/seedling/lib/birch/components/forms/fields/radio_group_field';
import { SelectionsHelper } from 'talent_matching/hooks/use_selections_helper';
import { withLocalePrefix } from 'shared/utils/translation';
import { standardRequestWithErrorFlash } from 'shared/utils/request';
import DataEntryModal from '@grnhse/seedling/lib/birch/components/modals/data_entry_modal';
import { Grid } from '@grnhse/seedling/lib/birch/layouts/grid_v2';
import {
  PrimaryButton,
  SecondaryButton,
} from '@grnhse/seedling/lib/birch/components/buttons';
import { WarningCallOut } from '@grnhse/seedling/lib/birch/components/call_out';
import { useTalentMatchingTabContext } from 'talent_matching/contexts/talent_matching_tab_context';
import { useTrackBulkAction } from 'talent_matching/lib/use_track_bulk_action';
import FeedbackTextField from './feedback_textfield';
import {
  StyledRadioGroup,
  candidateRatingOptions,
} from 'talent_matching/components/modals/leave_feedback_modal';

const t = withLocalePrefix('talent_matching.bulk_actions.feedback');

type Props = {
  isOpen: boolean;
  closeModal: () => void;
  selectionsHelper: SelectionsHelper;
};

const defaultValues = {
  candidate_rating_id: candidateRatingOptions[0].value,
  notes: '',
  notes_with_tags: '',
};

type FormData = {
  candidate_rating_id: { value: number; label: string };
  notes: string;
  notes_with_tags: string;
};

const FeedbackModal = ({ isOpen, closeModal, selectionsHelper }: Props) => {
  const {
    paths: { bulkFeedbackPath, hiringPlanTagsPath },
    appReviewSessionId,
  } = useTalentMatchingTabContext();
  const trackBulk = useTrackBulkAction('tm_app_review', appReviewSessionId);

  const onSubmit = async (data: FormData) => {
    const payload = {
      candidate_rating_id: data.candidate_rating_id,
      notes: data.notes,
      notes_with_tags: data.notes,
      application_ids: selectionsHelper.applicationIds,
    };

    const res = await standardRequestWithErrorFlash.post(
      bulkFeedbackPath,
      payload,
      {
        successMessage: t('success'),
      }
    );

    if (res.data.status === 'success') {
      // Await tracking before the reload, which would otherwise cancel the
      // in-flight analytics request.
      await trackBulk('leave_feedback', selectionsHelper.applicationIds);
      window.location.reload();
    }

    closeModal();
  };

  return (
    <DataEntryModal
      isOpen={isOpen}
      size="medium"
      onDismiss={closeModal}
      ariaLabel={'leave-feedback-modal'}
    >
      <Form onSubmit={onSubmit} defaultValues={defaultValues}>
        <DataEntryModal.Header onClick={closeModal}>
          {t('title', { count: selectionsHelper.totalSelected })}
        </DataEntryModal.Header>
        <DataEntryModal.Body>
          <StyledRadioGroup>
            <RadioGroupField
              name="candidate_rating_id"
              ariaLabel={t('labels.rating')}
              options={candidateRatingOptions}
              direction="row"
            />
            <WarningCallOut bodyText={t('callouts.mention_callout')} />
            <FeedbackTextField getTagsUrl={hiringPlanTagsPath} />
          </StyledRadioGroup>
        </DataEntryModal.Body>
        <DataEntryModal.Footer>
          <Grid container spacing={1}>
            <Grid>
              <SecondaryButton onClick={closeModal}>
                {t('actions.cancel')}
              </SecondaryButton>
            </Grid>
            <Grid>
              <PrimaryButton type="submit">{t('actions.save')}</PrimaryButton>
            </Grid>
          </Grid>
        </DataEntryModal.Footer>
      </Form>
    </DataEntryModal>
  );
};

export default FeedbackModal;
